import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { canManageBilling as appKitCanManageBilling } from '@lumi/app-kit';
import { fetchTenants } from '../utils/tenants';
import { setStoredTenantSlug, tenantSlugFromWorkspaceHost } from '../utils/runtime-config';
import { readPickedTenantId, writePickedTenantId } from '../utils/pickedTenantStorage';
import { takeRequestedTenantSlug } from '../utils/requestedTenant';
import { forgetStatutoryRulesAccess } from '../utils/statutoryRulesAccess';
import { forgetTrainingOccupationsAccess } from '../utils/trainingOccupationsAccess';
import { forgetSetupEntries } from '../utils/setupEntriesCache';
import { useAuthStore } from './auth';

export type TenantModule = { moduleKey: string; status: string };
export type TenantAssignment = { moduleKey: string; effect: string; roleKey: string | null };
export type Tenant = {
  id: string;
  slug: string;
  name: string;
  status: string;
  plan: string;
  // Payments enforcement stage (issue 213), mirrored from the master tenant row so
  // every member (not just billing managers) can render the read-only billing
  // banner. `null` when the tenant is in good standing.
  enforcementStage?: string | null;
  // The tenant owner (createdByUserId === me). Billing gates on this OR the
  // billing_manager role below, matching payment-api's TenantAdminGuard.
  isOwner?: boolean;
  // Holds the tenant-local `billing_manager` role — may manage billing without
  // being the owner. Resolved by user-api from the mirrored role assignments.
  isBillingManager?: boolean;
  // Holds the `suite_admin` suite role (ADR 0033) — the workspace admin. The
  // owner holds it too (materialized at provisioning), so gating on
  // isOwner || isSuiteAdmin mirrors user-api's ensureTenantAdmin.
  isSuiteAdmin?: boolean;
  // Holds the `data_protection` suite role (ADR 0033) — the workspace's review
  // authority for data subject requests and erasures. Reported by user-api as a
  // raw flag ALONGSIDE the resolved owner-OR-holder authority, precisely so a
  // surface whose server gate is the ROLE ALONE (issue 221, D47) can stand on
  // the same standing instead of re-deriving one. It is not the owner-OR-holder
  // decision and must never be OR'd with isOwner here.
  isDataProtection?: boolean;
  // The RESOLVED data-protection authority (D4): the owner OR the
  // `data_protection` role holder, decided by user-api's `dataProtectionAuthority()`
  // — the same function behind the internal membership answer privacy-api's
  // PrivacyAuthorityGuard consumes. The data subject request list (issue 789)
  // gates on it, so the page opens for exactly the set privacy-api serves; it is
  // read as-is and never rebuilt here from isOwner and isDataProtection.
  isDataProtectionAuthority?: boolean;
  // Consented temporary support access (ADR 0041): TRUE only when this workspace
  // is reachable through an approved support grant rather than a real membership.
  // A borrowed workspace is badged so a supporter can never mistake it for their
  // own, and so the badge disappears the moment the grant lapses (user-api stops
  // returning the tenant at all).
  supportAccess?: boolean;
  supportAccessExpiresAt?: string | null;
  modules?: TenantModule[];
  // This person hid the "Lumi einrichten" panel on Suite Home in this workspace
  // (per person + workspace, so it follows them to every device).
  setupPanelHidden?: boolean;
  // The showroom (#409 M3): seeder modules the workspace has not booked but may
  // open while its demo lasts. Beside `modules`, never a row in it.
  showroomModules?: string[];
  assignments?: TenantAssignment[];
};

// Shared tenant state for the homebase: the chrome (header tenant name/tabs,
// sidebar admin switcher) and the WorkspaceView content both read the SAME active
// tenant from here, so switching a tab reflows the panels. On a per-tenant host
// (`{slug}.{domain}`) exactly one tenant is visible; on the bare/dev host the full
// list drives the header tab bar.
export const useTenantsStore = defineStore('tenants', () => {
  const tenants = ref<Tenant[]>([]);
  const activeTenantId = ref<string | null>(null);
  const loading = ref(false);
  const error = ref(false);
  const loaded = ref(false);
  // The host we resolved from `{slug}.{domain}` — empty on the bare/dev host.
  const hostTenantSlug = tenantSlugFromWorkspaceHost();
  // Authenticated but not a member of this per-tenant host → caller logs out.
  const nonMember = ref(false);
  // Bumped whenever the tenant-scoped data the workspace renders must be re-read
  // from scratch (#364 F8/F9a): a tenant switch, and the end of suite setup, where
  // the demo the user was looking at has just been wiped server-side. Nothing about
  // either event remounts the workspace — the switch happens in place and the setup
  // wizard is a MODAL over it, so its `router.replace('/')` only drops a query
  // parameter — and every panel that fetches on mount alone would otherwise keep
  // showing the previous tenant's (or the wiped demo's) data until a section change.
  // Consumers watch this alongside `activeTenantId`.
  const dataVersion = ref(0);

  const activeTenant = computed<Tenant | null>(
    () => tenants.value.find((tn) => tn.id === activeTenantId.value) ?? tenants.value[0] ?? null,
  );
  const isSingleTenant = computed(() => tenants.value.length === 1);
  const isMultiTenant = computed(() => tenants.value.length > 1);

  // The workspace admin gate (ADR 0033): the owner or a suite_admin holder —
  // exactly the set that passes user-api's ensureTenantAdmin, so the admin
  // surface this unlocks (the Administration section) never 403s behind the nav. The
  // pre-issue-211 `*_admin`-suffix heuristic is retired: module admins (ats_admin,
  // time_admin, …) administer their module, not the workspace. The empty-tenant
  // bootstrap fallback is the owner, not any member.
  function hasAdminRole(tenant: Tenant | null): boolean {
    if (!tenant) return false;
    return tenant.isOwner === true || tenant.isSuiteAdmin === true;
  }

  // Owner-only gate for surfaces payment-api restricts to the tenant owner (its
  // TenantAdminGuard = membership && isOwner). A module *_admin is NOT enough for
  // billing, so gating those tabs on hasAdminRole showed a Billing tab that always
  // 403'd for non-owner admins. Falls back to hasAdminRole ONLY when the payload
  // predates the isOwner field, so older backends don't hide it from real owners.
  function isTenantOwner(tenant: Tenant | null): boolean {
    if (!tenant) return false;
    if (typeof tenant.isOwner === 'boolean') return tenant.isOwner;
    return hasAdminRole(tenant);
  }

  // May the user manage billing? The billing-manager set is ONE set (ADR 0033),
  // not to be re-narrowed on a single surface: the tenant OWNER, any holder of
  // the `billing_manager` role, OR a `suite_admin` — exactly the three-way check
  // payment-api's own hasUserTenantAdminMembership already performs. Single
  // source for the Billing nav item AND the BillingView access guard, so the
  // nav, the page, and the backend all agree (no "nav shown + blank page").
  //
  // The rule itself now lives in @lumi/app-kit (issue 261), because the module
  // teasers ask the same question from the OTHER six UIs, which cannot import
  // this store. This wrapper stays as the store's vocabulary; it must never grow
  // a second opinion — a re-derived role test here is exactly the drift the
  // shared predicate exists to prevent.
  function canManageBilling(tenant: Tenant | null): boolean {
    return appKitCanManageBilling(tenant);
  }

  // Is the active tenant in the payments lockout state (issue 213)? The ladder's
  // `blocked` stage suspends the tenant (Tenant.status='suspended'); user-api's login
  // decision treats blocked/suspended as one lockout. workspace-ui is the one app that
  // still lets a suspended member in (no login refusal) so they can be told what
  // happened — so it is the one place that must recognize the state and show the lock
  // screen instead of dead module tiles. Single source for AppChrome's shell branch.
  function isLocked(tenant: Tenant | null): boolean {
    return !!tenant && (tenant.status === 'suspended' || tenant.status === 'blocked');
  }

  // Mirror a successful hide/show of the setup panel into the loaded list, so a
  // trip away from Suite Home and back does not bring the panel back.
  function setSetupPanelHidden(tenantId: string, hidden: boolean) {
    const row = tenants.value.find((tn) => tn.id === tenantId);
    if (row) row.setupPanelHidden = hidden;
  }

  // On the bare/dev host, persist the chosen tenant as the current context so the
  // same-origin fan-out (X-Tenant-Slug) + notifications scope to it. On a per-tenant
  // host the subdomain always wins in currentTenantSlug(), so this is inert there.
  function scopeToTenant(slug: string) {
    setStoredTenantSlug(slug);
  }

  // Remember the pick for the next page load, stamped with the signed-in user so
  // the next account in this browser cannot inherit it (see pickedTenantStorage).
  function rememberPick(tenantId: string) {
    const authStore = useAuthStore();
    writePickedTenantId(authStore.user?.userId, tenantId);
  }

  /** Ask every tenant-scoped panel to re-read itself (see `dataVersion`). */
  function invalidate() {
    // The session's statutory-rules and training-occupations answers go first,
    // before any watcher of `dataVersion` asks again: a role given or taken
    // since must count.
    forgetStatutoryRulesAccess();
    forgetTrainingOccupationsAccess();
    forgetSetupEntries();
    dataVersion.value += 1;
  }

  /** Re-read the membership list AND every tenant-scoped panel. */
  async function refresh() {
    await load(true);
    invalidate();
  }

  async function load(force = false) {
    // The tenant list is membership-gated — without a session the request only 401s,
    // and the global 401 handler would bounce the app to /login in a reload loop
    // (issue 213). Callers on authenticated routes always have a token; the /login
    // route must not fetch. Fail-safe against every load() caller, not just the banner.
    const authStore = useAuthStore();
    if (!authStore.token) return;
    if (loaded.value && !force) return;
    loading.value = true;
    error.value = false;
    nonMember.value = false;
    try {
      const all = (await fetchTenants(force)) as Tenant[];
      // Always list every tenant the user belongs to — the switcher offers them all,
      // in both host modes.
      if (hostTenantSlug) {
        const current = all.find((tn) => tn.slug === hostTenantSlug);
        if (!current) {
          // Authenticated but not a member of THIS host's tenant — treat as a failed
          // login (the caller drops the session and returns to /login).
          nonMember.value = true;
          tenants.value = [];
          return;
        }
        tenants.value = all;
        // On a per-tenant host the active tenant is fixed to the host's tenant;
        // switching to another goes through selectTenant → a cross-host redirect.
        activeTenantId.value = current.id;
      } else {
        tenants.value = all;
        // What the URL asked for outranks what this browser remembers. `?tenantSlug=`
        // is an explicit instruction — a module's switcher handoff, an SSO return, a
        // mailed link — and it used to lose to the remembered pick every time, so a
        // deep link into a second tenant opened the first one instead and rewrote the
        // fan-out scope to match. Consumed here (one-shot, see requestedTenant.ts), so
        // it governs this page load only; `rememberPick` below is what carries it
        // through the next reload.
        //
        // Honoured ONLY when the freshly fetched membership list contains it: a link
        // naming a tenant this account cannot see falls back to the default, exactly
        // like a remembered id that no longer describes a membership.
        const requestedSlug = takeRequestedTenantSlug();
        const requestedId = requestedSlug ? (all.find((tn) => tn.slug === requestedSlug)?.id ?? null) : null;
        if (requestedId) {
          activeTenantId.value = requestedId;
        } else if (!activeTenantId.value || !all.some((tn) => tn.id === activeTenantId.value)) {
          // A reload restores the tenant the user picked last (#364 F9b) — but only
          // when the stamp vouches for it (this user, this value) AND the freshly
          // fetched membership list still contains it. Anything else falls back to
          // the first membership: a remembered tenant this account cannot see must
          // never be rendered under this account's name.
          const authStore = useAuthStore();
          const remembered = readPickedTenantId(authStore.user?.userId);
          const restorable = remembered && all.some((tn) => tn.id === remembered) ? remembered : null;
          activeTenantId.value = restorable ?? all[0]?.id ?? null;
        }
        // Bare/dev host: scope the same-origin fan-out to the default active tenant.
        const active = all.find((tn) => tn.id === activeTenantId.value);
        if (active) {
          scopeToTenant(active.slug);
          rememberPick(active.id);
        }
      }
      loaded.value = true;
    } catch {
      error.value = true;
    } finally {
      loading.value = false;
    }
  }

  // Pick a tenant from the switcher. Returns the tenant to REDIRECT to when the
  // switch crosses hosts (per-tenant prod host → another tenant's host); returns
  // null when handled in place (already active, or a bare/dev same-origin re-scope
  // that reloads the panels via the activeTenantId watch).
  function selectTenant(t: Tenant): Tenant | null {
    if (t.id === activeTenantId.value) return null;
    if (hostTenantSlug && t.slug !== hostTenantSlug) return t;
    scopeToTenant(t.slug);
    activeTenantId.value = t.id;
    rememberPick(t.id);
    // The switch happens in place, so nothing remounts: tell the panels to re-read
    // (#364 F9a). Without this the previous tenant's dashboards, documents and
    // greeting stay on screen until a section change forces a reload.
    invalidate();
    return null;
  }

  return {
    tenants,
    activeTenantId,
    loading,
    error,
    loaded,
    nonMember,
    dataVersion,
    hostTenantSlug,
    activeTenant,
    isSingleTenant,
    isMultiTenant,
    hasAdminRole,
    isTenantOwner,
    canManageBilling,
    isLocked,
    setSetupPanelHidden,
    load,
    refresh,
    invalidate,
    selectTenant,
  };
});
